import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from './api';
import { Loader2, Mail } from 'lucide-react';
import { logger } from '../lib/logger';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email.'); return;
    }
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setDone(true);
    } catch (err) {
      logger.error('forgot-password failed', err);
      // For security, show the same success message even on errors that aren't 429.
      if (err.status === 429) setError(err.message || 'Too many requests. Try again later.');
      else setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white flex items-center justify-center px-5 py-10" data-testid="forgot-page">
      <div className="absolute inset-0 bg-[radial-gradient(800px_circle_at_30%_40%,rgba(0,76,84,0.18),transparent_60%)] pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-7">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white shadow-2xl mb-4">
            <img src={LOGO_URL} alt="Apex Athletic Mind" className="w-full h-full object-contain" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            Forgot your password?
          </h1>
          <p className="text-white/55 text-[14px] leading-relaxed max-w-[34ch]">
            Enter your email and we'll send you a link to reset it.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-7">
          {done ? (
            <div className="text-center space-y-3 py-2" data-testid="forgot-success">
              <div className="w-12 h-12 rounded-full bg-[#004C54]/30 border border-[#004C54]/50 flex items-center justify-center mx-auto">
                <Mail className="h-5 w-5 text-[#4FBCC7]" />
              </div>
              <div className="font-heading text-lg font-bold">Check your email</div>
              <p className="text-[14px] text-white/65 leading-relaxed">
                If an account exists for <span className="text-white font-semibold">{email}</span>, we just sent a reset link. It expires in 60 minutes.
              </p>
              <p className="text-[12px] text-white/40">
                Don't see it? Check your spam folder.
              </p>
              <Link to="/app/login" className="inline-block mt-2 text-[13px] text-[#4FBCC7] hover:text-white font-semibold" data-testid="forgot-back-to-login">
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
              <div>
                <Label htmlFor="forgot-email" className="text-[12px] text-white/60 mb-1.5 block">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                  disabled={submitting}
                  data-testid="forgot-email"
                  required
                />
              </div>

              {error && (
                <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
                data-testid="forgot-submit"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Send reset link'}
              </Button>

              <p className="text-center text-[12px] text-white/45 mt-3">
                Remembered it?{' '}
                <Link to="/app/login" className="text-[#4FBCC7] hover:text-white font-semibold" data-testid="forgot-login-link">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-5">
          <a href="/" className="text-sm text-white/50 hover:text-white transition-colors">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
